// src/comps/CreateAccount.jsx
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import EmailVerifyModal from "./EmailVerifyModal";
import { API_URL } from "../config";

const departments = [
  { label: "Architecture (Arch)", value: "Arch" },
  { label: "Biomedical Engineering (BME)", value: "BME" },
  { label: "Chemical Engineering (Ch.E)", value: "Ch.E" },
  { label: "Chemistry (CHEM)", value: "CHEM" },
  { label: "Civil Engineering (CE)", value: "CE" },
  { label: "Computer Science and Engineering (CSE)", value: "CSE" },
  { label: "Electrical and Electronic Engineering (EEE)", value: "EEE" },
  { label: "Humanities (HUM)", value: "HUM" },
  { label: "Industrial and Production Engineering (IPE)", value: "IPE" },
  { label: "Materials and Metallurgical Engineering (MME)", value: "MME" },
  { label: "Mathematics (MATH)", value: "MATH" },
  { label: "Mechanical Engineering (ME)", value: "ME" },
  { label: "Nanomaterials and Ceramic Engineering (NCE)", value: "NCE" },
  { label: "Naval Architecture and Marine Engineering (NAME)", value: "NAME" },
  { label: "Petroleum and Mineral Resources Engineering (PMRE)", value: "PMRE" },
  { label: "Physics (PHYS)", value: "PHYS" },
  { label: "Urban and Regional Planning (URP)", value: "URP" },
  { label: "Water Resources Engineering (WRE)", value: "WRE" },
];

// NEW: Residential Hall options
const residentialHalls = [
  { label: "Ladies Hall", value: "Ladies Hall" },
  { label: "Sher-E-Bangla Hall", value: "Sher-E-Bangla Hall" },
  { label: "Titumir Hall", value: "Titumir Hall" },
  { label: "Suhrawardy Hall", value: "Suhrawardy Hall" },
  { label: "M A Rashid Hall", value: "M A Rashid Hall" },
  { label: "Shaheed Smriti Hall", value: "Shaheed Smriti Hall" },
  { label: "Ahsanullah West Hall", value: "Ahsanullah West Hall" },
  { label: "Ahsanullah North Hall", value: "Ahsanullah North Hall" },
  { label: "Nazrul Islam Hall", value: "Nazrul Islam Hall" },
];

export default function CreateAccount() {
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
    dob: "",
    department: "",
    residentialHall: "", // ← NEW
    graduationYear: "",
    gradNotApplicable: false,
    membershipType: "general",
    streetAddress: "",
    aptSuite: "",
    city: "",
    stateProvince: "",
    postalCode: "",
    country: "",
  });
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
      ...(name === "gradNotApplicable" && checked ? {
        graduationYear: "",
        membershipType: "general"
      } : {}),
    }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setError("");

    if (form.password !== form.confirmPassword) {
      return setError("Passwords do not match");
    }
    if (form.gradNotApplicable && (form.membershipType === "lifetime" || form.membershipType === "registered")) {
      return setError(
        "Users without a graduation year can only be General Members"
      );
    }

    const payload = {
      firstName: form.firstName,
      lastName: form.lastName,
      email: form.email,
      phone: form.phone,
      password: form.password,
      dob: form.dob,
      department: form.department,
      residentialHall: form.residentialHall,
      graduationYear: form.gradNotApplicable ? null : Number(form.graduationYear),
      gradNotApplicable: form.gradNotApplicable,
      role: form.membershipType,
      address: {
        street: form.streetAddress,
        aptSuite: form.aptSuite,
        city: form.city,
        state: form.stateProvince,
        postalCode: form.postalCode,
        country: form.country,
      },
    };

    try {
      const res = await fetch(`${API_URL}/api/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.msg || "Registration failed");
      } else {
        setMessage(data.msg);
        // General members can login immediately, others need to pay first
        if (data.isGeneralMember) {
          // Show success message and redirect to login after a short delay
          setTimeout(() => {
            navigate("/login");
          }, 2000);
        } else {
          // Registered/Lifetime members need to complete payment
          setShowVerifyModal(true);
        }
      }
    } catch {
      setError("Server error, please try again");
    }
  };

  return (
    <div className="min-h-screen flex flex-col justify-center items-center bg-white">
      <h1 className="text-4xl font-bold mb-6">Create Account</h1>
      <div className="bg-white p-8 rounded shadow-md w-full max-w-2xl">
        {error && <div className="mb-4 text-center text-red-600">{error}</div>}
        {message && (
          <div className="mb-4 text-center text-green-600">{message}</div>
        )}

        <form onSubmit={handleCreate} className="space-y-4">
          {/* Name */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="firstName" className="block text-black mb-2">
                First Name
              </label>
              <input
                id="firstName"
                name="firstName"
                type="text"
                required
                className="border w-full p-2"
                value={form.firstName}
                onChange={handleChange}
              />
            </div>
            <div>
              <label htmlFor="lastName" className="block text-black mb-2">
                Last Name
              </label>
              <input
                id="lastName"
                name="lastName"
                type="text"
                required
                className="border w-full p-2"
                value={form.lastName}
                onChange={handleChange}
              />
            </div>
          </div>

          {/* Contact */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="email" className="block text-black mb-2">
                Email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                className="border w-full p-2"
                value={form.email}
                onChange={handleChange}
              />
            </div>
            <div>
              <label htmlFor="phone" className="block text-black mb-2">
                Phone
              </label>
              <input
                id="phone"
                name="phone"
                type="tel"
                className="border w-full p-2"
                value={form.phone}
                onChange={handleChange}
              />
            </div>
          </div>

          {/* Password */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="password" className="block text-black mb-2">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                required
                className="border w-full p-2"
                value={form.password}
                onChange={handleChange}
              />
            </div>
            <div>
              <label htmlFor="confirmPassword" className="block text-black mb-2">
                Confirm Password
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                required
                className="border w-full p-2"
                value={form.confirmPassword}
                onChange={handleChange}
              />
            </div>
          </div>

          {/* DOB & Department */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="dob" className="block text-black mb-2">
                Date of Birth
              </label>
              <input
                id="dob"
                name="dob"
                type="date"
                required
                className="border w-full p-2"
                value={form.dob}
                onChange={handleChange}
              />
            </div>
            <div>
              <label htmlFor="department" className="block text-black mb-2">
                BUET Department
              </label>
              <select
                id="department"
                name="department"
                required
                className="border w-full p-2"
                value={form.department}
                onChange={handleChange}
              >
                <option value="">Select...</option>
                {departments.map((d) => (
                  <option key={d.value} value={d.value}>
                    {d.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Residential Hall */}
          <div>
            <label htmlFor="residentialHall" className="block text-black mb-2">
              Residential Hall
            </label>
            <select
              id="residentialHall"
              name="residentialHall"
              required
              className="border w-full p-2"
              value={form.residentialHall}
              onChange={handleChange}
            >
              <option value="">Select...</option>
              {residentialHalls.map((h) => (
                <option key={h.value} value={h.value}>
                  {h.label}
                </option>
              ))}
            </select>
          </div>

          {/* Graduation Year */}
          <div className="flex items-center space-x-4">
            <div className="flex-1">
              <label htmlFor="graduationYear" className="block text-black mb-2">
                BUET Graduation Year
              </label>
              <input
                id="graduationYear"
                name="graduationYear"
                type="number"
                min="1900"
                max={new Date().getFullYear()}
                disabled={form.gradNotApplicable}
                required={!form.gradNotApplicable}
                className={`border w-full p-2 ${
                  form.gradNotApplicable ? "bg-gray-100" : ""
                }`}
                value={form.graduationYear}
                onChange={handleChange}
              />
            </div>
            <label className="flex items-center space-x-2 mt-6">
              <input
                type="checkbox"
                name="gradNotApplicable"
                checked={form.gradNotApplicable}
                onChange={handleChange}
                className="h-4 w-4 text-[#800000] border-gray-300 rounded"
              />
              <span>Not Applicable</span>
            </label>
          </div>

          {/* Membership Type */}
          <div>
            <div className="flex items-center mb-2">
              <label className="block font-medium">Membership Type</label>
              <div className="relative ml-2 group">
                <span className="cursor-help text-blue-600 text-sm border border-blue-600 rounded-full w-4 h-4 flex items-center justify-center">i</span>
                <div className="absolute left-0 top-6 hidden group-hover:block bg-gray-800 text-white text-xs rounded-lg p-3 w-64 z-10">
                  <div className="mb-2"><strong>General:</strong> No application fee required</div>
                  <div className="mb-2"><strong>Registered:</strong> No application fee, yearly fees apply (BUET graduates only)</div>
                  <div><strong>Lifetime:</strong> One-time fee, no yearly fees (BUET graduates only)</div>
                </div>
              </div>
            </div>
            <div className="flex flex-col space-y-3">
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  name="membershipType"
                  value="general"
                  checked={form.membershipType === "general"}
                  onChange={handleChange}
                  className="h-4 w-4 text-[#800000] border-gray-300"
                />
                <span className="ml-2">General Member</span>
              </label>
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  name="membershipType"
                  value="registered"
                  checked={form.membershipType === "registered"}
                  onChange={handleChange}
                  disabled={form.gradNotApplicable}
                  className="h-4 w-4 text-[#800000] border-gray-300"
                />
                <span className="ml-2">
                  Registered Member
                  {form.gradNotApplicable && (
                    <span className="ml-1 text-sm text-gray-500">
                      (disabled)
                    </span>
                  )}
                </span>
              </label>
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  name="membershipType"
                  value="lifetime"
                  checked={form.membershipType === "lifetime"}
                  onChange={handleChange}
                  disabled={form.gradNotApplicable}
                  className="h-4 w-4 text-[#800000] border-gray-300"
                />
                <span className="ml-2">
                  Lifetime Member
                  {form.gradNotApplicable && (
                    <span className="ml-1 text-sm text-gray-500">
                      (disabled)
                    </span>
                  )}
                </span>
              </label>
            </div>
          </div>

          {/* Address */}
          <h2 className="text-xl font-semibold mt-6 mb-2">Address</h2>
          <div className="space-y-4">
            <div>
              <label htmlFor="streetAddress" className="block text-black mb-2">
                Street Address
              </label>
              <input
                id="streetAddress"
                name="streetAddress"
                type="text"
                required
                className="border w-full p-2"
                value={form.streetAddress}
                onChange={handleChange}
              />
            </div>
            <div>
              <label htmlFor="aptSuite" className="block text-black mb-2">
                Apt/Suite #
              </label>
              <input
                id="aptSuite"
                name="aptSuite"
                type="text"
                className="border w-full p-2"
                value={form.aptSuite}
                onChange={handleChange}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="city" className="block text-black mb-2">
                  City
                </label>
                <input
                  id="city"
                  name="city"
                  type="text"
                  required
                  className="border w-full p-2"
                  value={form.city}
                  onChange={handleChange}
                />
              </div>
              <div>
                <label htmlFor="stateProvince" className="block text-black mb-2">
                  State/Province/Region
                </label>
                <input
                  id="stateProvince"
                  name="stateProvince"
                  type="text"
                  className="border w-full p-2"
                  value={form.stateProvince}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="postalCode" className="block text-black mb-2">
                  ZIP/Postal Code
                </label>
                <input
                  id="postalCode"
                  name="postalCode"
                  type="text"
                  required
                  className="border w-full p-2"
                  value={form.postalCode}
                  onChange={handleChange}
                />
              </div>
              <div>
                <label htmlFor="country" className="block text-black mb-2">
                  Country
                </label>
                <input
                  id="country"
                  name="country"
                  type="text"
                  required
                  className="border w-full p-2"
                  value={form.country}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>

          <button
            type="submit"
            className="w-full bg-[#800000] text-white font-bold py-2 px-4 rounded hover:bg-[#9b0000] transition mt-6"
          >
            Create Account
          </button>
        </form>
      </div>

      {showVerifyModal && (
        <EmailVerifyModal
          onConfirm={() => navigate("/payment-confirmation")}
          onRetry={() => navigate("/")}
        />
      )}
    </div>
  );
}
